/**
 * Multi-calendar bookkeeping for the task calendar.
 *
 * The calendar draws one visual calendar per task CATEGORY rather than
 * one shared timeline, because tasks of different categories do not
 * block each other: a clean and a delivery at 09:00 are not in conflict,
 * and drawing them in one lane asserts that they are. Each category is
 * independently toggleable and colour-coded.
 *
 * Visibility is a pure display preference and lives only in
 * `localStorage` — no server round-trip. The key shape follows
 * `state/user-preferences.ts` (`nx:<thing>:<scope-id>`); the scope here
 * is the SITE rather than the user, because which calendars are worth
 * looking at is a property of the building, not of the person.
 */
import type { TaskCategory, TaskRequest } from '../../api/v1';
import { cleanModeOfRequest, DEFAULT_CLEAN_MODE, type CleanMode } from './clean-modes';

/**
 * One calendar per TASK TYPE: the v1 categories, except that `clean` is
 * four -- Vacuum, Sweep, Dust-mop and Scrub are separate task types to the
 * people booking them (Akash, 2026-10-22), so each gets its own lane,
 * colour and toggle, although all three are v1 category `clean`.
 */
export type CalendarLane = Exclude<TaskCategory, 'clean'> | CleanMode;

export const TASK_CATEGORIES: readonly CalendarLane[] = [
  'vacuum',
  'sweep',
  'dust_mop',
  'scrub',
  'patrol',
  'delivery',
  'station_docking',
  // Dispatchable, unlike `charge` below: an operator asks for it, the
  // robot parks at a DustBagStation and waits to be told the bag was
  // emptied. It gets its own calendar for the same reason every other
  // category does -- a dustbag and a clean at 09:00 are not in
  // conflict -- and its own colour so a lane of them is legible.
  'dustbag',
  'charge',
  'custom',
];

// `station_docking` and `charge` carry the same labels the dispatch
// form's catalog uses ("Station", "Charge" — `task_catalog.py::_LABELS`
// server-side), so the thing an operator books and the thing the
// calendar draws are named identically.
export const CATEGORY_LABEL: Record<CalendarLane, string> = {
  vacuum: 'Vacuum',
  sweep: 'Sweep',
  dust_mop: 'Dust-mop',
  scrub: 'Scrub',
  patrol: 'Patrol',
  delivery: 'Delivery',
  station_docking: 'Station',
  // The `dustbag` category's name since 2026-10-22.
  dustbag: 'Routine check',
  charge: 'Charge',
  custom: 'Other',
};

/**
 * What a task is DRAWN as: its type, except that a delivery run by a
 * linen robot is a "Linen run" (pink, "Ln"; Akash, 2026-10-30). A linen
 * robot is one whose robot type is Linen, or that sits in a group named
 * Linen -- so it follows the site's editable robot groups
 * (`calendar/robot-groups.ts::linenRobotKeys`). It is a paint, not a
 * category: a linen run is still a `delivery` to RMF and to the dispatch
 * form. The Month legend lists it as a type of its own (2026-10-31).
 */
export type LanePaint = CalendarLane | 'linen';

export const LINEN_LABEL = 'Linen run';

/** `lane`, or `linen` for a delivery whose robot is a linen robot. */
export function paintFor(
  lane: CalendarLane,
  robotKey: string | null | undefined,
  linenRobots: ReadonlySet<string> | undefined,
): LanePaint {
  return lane === 'delivery' && robotKey && linenRobots?.has(robotKey) ? 'linen' : lane;
}

export type CalendarVisibility = Record<CalendarLane, boolean>;

export const ALL_CALENDARS_VISIBLE: CalendarVisibility = {
  vacuum: true,
  sweep: true,
  dust_mop: true,
  scrub: true,
  patrol: true,
  delivery: true,
  station_docking: true,
  dustbag: true,
  charge: true,
  custom: true,
};

/**
 * `TaskState.type` is RMF's own category string, round-tripped from
 * `TaskSummary.category`. Anything the v1 enum does not name is real
 * data from a fleet we do not model, so it lands on `custom` rather
 * than being dropped off the calendar entirely. `station` is upstream
 * RMF's shorter spelling of `station_docking`, which the dispatch form
 * also still uses internally.
 */
export function categoryOf(type: string, cleanMode?: CleanMode | null): CalendarLane {
  if (type === 'station') return 'station_docking';
  // A clean is drawn in its mode's lane; one whose mode is unknown (a
  // queued task, which carries no phases yet, or a pre-mode clean) in
  // Vacuum's, the default.
  if (type === 'clean') return cleanMode ?? DEFAULT_CLEAN_MODE;
  return (TASK_CATEGORIES as readonly string[]).includes(type)
    ? (type as CalendarLane)
    : 'custom';
}

/** The lane a SCHEDULE (and each of its occurrences) is drawn in: its
 *  category, and for a clean the mode its stored task asks for. */
export function laneOfSchedule(s: {
  readonly category: TaskCategory;
  readonly task?: Pick<TaskRequest, 'category' | 'phases'> | null;
}): CalendarLane {
  return categoryOf(s.category, s.category === 'clean' ? cleanModeOfRequest(s.task) : undefined);
}

const STORAGE_PREFIX = 'nx:task-calendars';

function storageKey(siteId: string): string {
  return `${STORAGE_PREFIX}:${siteId}`;
}

export function loadCalendarVisibility(siteId: string): CalendarVisibility {
  if (typeof window === 'undefined' || !siteId) return ALL_CALENDARS_VISIBLE;
  try {
    const raw = window.localStorage.getItem(storageKey(siteId));
    if (!raw) return ALL_CALENDARS_VISIBLE;
    return mergeVisibility(JSON.parse(raw));
  } catch {
    return ALL_CALENDARS_VISIBLE;
  }
}

export function saveCalendarVisibility(siteId: string, visibility: CalendarVisibility): void {
  if (typeof window === 'undefined' || !siteId) return;
  try {
    window.localStorage.setItem(storageKey(siteId), JSON.stringify(visibility));
  } catch {
    // localStorage may be unavailable (private mode, quota); a lost
    // display preference is not worth failing the render over.
  }
}

/** Unknown keys are ignored and missing ones default to visible, so a
 *  category added in a later release does not arrive switched off for
 *  everyone who already has a stored preference. */
function mergeVisibility(loaded: unknown): CalendarVisibility {
  if (!loaded || typeof loaded !== 'object') return ALL_CALENDARS_VISIBLE;
  const o = loaded as Record<string, unknown>;
  const out = { ...ALL_CALENDARS_VISIBLE };
  // A preference stored when cleaning was ONE calendar (`clean`) carries
  // over to all three of the lanes it became, so a hidden Cleaning
  // calendar stays hidden.
  if (typeof o.clean === 'boolean') {
    out.vacuum = out.sweep = out.dust_mop = out.scrub = o.clean;
  }
  // ...and one stored while Scrub was called Wet scrub carries over to Scrub.
  if (typeof o.wet_scrub === 'boolean') out.scrub = o.wet_scrub;
  for (const cat of TASK_CATEGORIES) {
    if (typeof o[cat] === 'boolean') out[cat] = o[cat] as boolean;
  }
  return out;
}
